<template>
  <app-page
    :title="program?.program_name || 'Award'"
    :previous="[{ label: 'Recognition', to: { name: 'recognition' } }]"
    back-route="/recognition"
  >
    <template #header-content>
      <div v-if="program && program.budget_minor" class="budget-card">
        <div class="budget-card__header">
          <span class="budget-card__label">Budget</span>
        </div>
        <div class="budget-card__amount">
          <span class="budget-card__value">{{ formatCurrency(budgetRemaining) }}</span>
          <span class="budget-card__suffix">remaining</span>
        </div>
        <div class="budget-card__progress">
          <b-progress :value="budgetSpent" :max="program.budget_minor" class="budget-card__bar" />
        </div>
        <div class="budget-card__footer">
          <span class="budget-card__total">{{ formatCurrency(program.budget_minor) }} Budget</span>
          <span class="budget-card__spent">{{ formatCurrency(budgetSpent) }} Spent</span>
        </div>
      </div>
    </template>

    <template #custom-content>
      <app-loading :loading="isLoading" is-background />

      <template v-if="!isLoading && program">
        <b-col>
          <sme-card large>
            <navigable-tabs content-class="mt-4">
              <b-tab id="configuration">
                <template #title>
                  <span>Configuration</span>
                </template>
                <open-award-configuration-tab v-if="isOpen" :program="program" @updated="onProgramUpdated" />
                <program-award-configuration-tab v-else-if="isProgram" :program="program" @updated="onProgramUpdated" />
                <milestone-award-configuration-tab
                  v-else-if="isMilestone"
                  :program="program"
                  @updated="onProgramUpdated"
                />
              </b-tab>

              <b-tab id="pending-approval" @shown="onPendingTabShown">
                <template #title>
                  <span>
                    Pending approval
                    <b-badge v-if="pendingNominations.length > 0" pill variant="primary" class="ml-1">
                      {{ pendingNominations.length }}
                    </b-badge>
                  </span>
                </template>
                <pending-approvals-table
                  :nominations="pendingNominations"
                  :loading="isLoadingPendingNominations"
                  :show-program-column="false"
                />
              </b-tab>

              <b-tab id="upcoming" v-if="isMilestone" @shown="onUpcomingTabShown">
                <template #title>
                  <span>
                    Upcoming
                    <b-badge v-if="upcomingAwards.length > 0" pill variant="primary" class="ml-1">
                      {{ upcomingAwards.length }}
                    </b-badge>
                  </span>
                </template>
                <upcoming-awards-table :awards="upcomingAwards" :loading="isLoadingUpcomingAwards" />
              </b-tab>

              <b-tab id="transactions">
                <template #title>
                  <span>Transactions</span>
                </template>
                <transactions-table :programs="programs" :program-id="programId" />
              </b-tab>
            </navigable-tabs>
          </sme-card>
        </b-col>
      </template>

      <template v-if="!isLoading && !program">
        <b-col>
          <b-card class="text-center p-5">
            <p class="text-muted">Award not found.</p>
            <router-link :to="{ name: 'recognition' }">Return to Recognition</router-link>
          </b-card>
        </b-col>
      </template>
    </template>
  </app-page>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import AppPage from '@/components/AppPage.vue';
import AppLoading from '@/components/AppLoading.vue';
import NavigableTabs from '@/components/NavigableTabs.vue';
import SmeCard from '@/components/atoms/SmeCard.vue';
import PendingApprovalsTable from '@/pages/recognition/components/PendingApprovalsTable.vue';
import UpcomingAwardsTable from '@/pages/recognition/components/UpcomingAwardsTable.vue';
import TransactionsTable from '@/pages/recognition/components/TransactionsTable.vue';
import OpenAwardConfigurationTab from '@/pages/recognition/components/OpenAwardConfigurationTab.vue';
import ProgramAwardConfigurationTab from '@/pages/recognition/components/ProgramAwardConfigurationTab.vue';
import MilestoneAwardConfigurationTab from '@/pages/recognition/components/MilestoneAwardConfigurationTab.vue';
import { toCurrency } from '@/utils';
import ApiClient from '@/ApiClient';
import State from '@/state/State';

const props = defineProps({
  programId: {
    type: String,
    required: true,
  },
});

const program = ref(null);
const programs = ref([]);
const pendingNominations = ref([]);
const upcomingAwards = ref([]);
const isLoadingProgram = ref(true);
const isLoadingPendingNominations = ref(false);
const isLoadingUpcomingAwards = ref(false);
const pendingNominationsLoaded = ref(false);
const upcomingAwardsLoaded = ref(false);

const isLoading = computed(() => isLoadingProgram.value);
const currency = computed(() => State.state?.company?.default_currency);

const isMilestone = computed(() => {
  return program.value?.program_type === 'MILESTONE';
});

const isOpen = computed(() => {
  return program.value?.program_type === 'OPEN';
});

const isProgram = computed(() => {
  return program.value?.program_type === 'PROGRAM';
});

const budgetSpent = computed(() => {
  if (!program.value?.budget_minor) {
    return 0;
  }
  const properties = program.value.properties || {};
  return properties.budget_spent_minor || 0;
});

const budgetRemaining = computed(() => {
  if (!program.value?.budget_minor) {
    return 0;
  }
  return program.value.budget_minor - budgetSpent.value;
});

const formatCurrency = value => {
  return toCurrency(value, currency.value).replace(/\.00$/, '');
};

const getProgram = async () => {
  isLoadingProgram.value = true;
  try {
    const result = await ApiClient.getRecognitionProgram(props.programId);
    program.value = result;
    // Create a single-item array for the TransactionsTable programs prop
    programs.value = [result];
  } catch (e) {
    program.value = null;
    programs.value = [];
  } finally {
    isLoadingProgram.value = false;
  }
};

const getPendingNominations = async () => {
  isLoadingPendingNominations.value = true;
  try {
    const nominations = await ApiClient.getPendingNominations(props.programId);
    pendingNominations.value = nominations || [];
    pendingNominationsLoaded.value = true;
  } catch (e) {
    pendingNominations.value = [];
  } finally {
    isLoadingPendingNominations.value = false;
  }
};

const onPendingTabShown = () => {
  if (!pendingNominationsLoaded.value) {
    getPendingNominations();
  }
};

const getUpcomingAwards = async () => {
  isLoadingUpcomingAwards.value = true;
  try {
    const awards = await ApiClient.getUpcomingMilestoneAwards(props.programId);
    upcomingAwards.value = awards || [];
    upcomingAwardsLoaded.value = true;
  } catch (e) {
    upcomingAwards.value = [];
  } finally {
    isLoadingUpcomingAwards.value = false;
  }
};

const onUpcomingTabShown = () => {
  if (!upcomingAwardsLoaded.value) {
    getUpcomingAwards();
  }
};

const onProgramUpdated = () => {
  // Refresh program data after successful update
  getProgram();
};

const loadAllData = async () => {
  // Reset loaded flags
  pendingNominationsLoaded.value = false;
  upcomingAwardsLoaded.value = false;

  await getProgram();
  // Pre-load pending nominations
  await getPendingNominations();
  // Pre-load upcoming for milestone awards
  if (isMilestone.value) {
    await getUpcomingAwards();
  }
};

// Watch for programId changes (Vue Router may reuse this component)
watch(
  () => props.programId,
  () => {
    loadAllData();
  },
);

onMounted(() => {
  loadAllData();
});
</script>

<style scoped lang="scss">
.budget-card {
  background: var(--palette-color-base-white);
  border-radius: 0.5rem;
  padding: 1.25rem;
  min-width: 250px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);

  &__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 0.5rem;
  }

  &__label {
    font-size: 0.875rem;
    color: var(--palette-color-default-lighten-30);
  }

  &__amount {
    display: flex;
    align-items: baseline;
    gap: 0.375rem;
    margin-bottom: 0.75rem;
  }

  &__value {
    font-size: 2rem;
    font-weight: 600;
    color: var(--palette-color-default);
  }

  &__suffix {
    font-size: 0.875rem;
    color: var(--palette-color-default-lighten-30);
  }

  &__progress {
    margin-bottom: 0.75rem;
  }

  &__bar {
    height: 0.5rem;
    border-radius: 0.25rem;

    :deep(.progress-bar) {
      background-color: var(--palette-color-brand-primary);
    }
  }

  &__footer {
    display: flex;
    justify-content: space-between;
    font-size: 0.75rem;
  }

  &__total {
    color: var(--palette-color-default-lighten-30);
  }

  &__spent {
    color: var(--palette-color-brand-primary);
  }
}

.tab-placeholder {
  padding: 3rem;
  text-align: center;
}
</style>
